import { useState } from "react";
import { Link } from "react-router-dom";
import { PageLayout } from "@/components/layout";
import { blogPosts, blogCategories, type BlogCategory } from "@/data/blog-posts";
import { useClientTranslation } from "@/hooks/use-client-translation";
import { ChevronRight } from "lucide-react";

/* -------------------------------------------------------------------------- */
/*                              Blog Card                                     */
/* -------------------------------------------------------------------------- */

interface BlogCardProps {
  slug: string;
  title: string;
  description: string;
  category: BlogCategory;
  readTime: string;
  image?: string;
}

function BlogCard({
  slug,
  title,
  description,
  category,
  readTime,
  image,
}: BlogCardProps) {
  const { t } = useClientTranslation();
  return (
    <Link
      to={`/blog/${slug}`}
      className="group flex flex-col overflow-hidden rounded-lg border border-black/15 bg-white h-full"
    >
      {/* Image */}
      <div className="aspect-[616/300] w-full overflow-hidden bg-gray-100 shrink-0">
        {image ? (
          <img
            src={image}
            alt={title}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-purple-primary/5 to-purple-primary/10">
            <img
              src="/mezepay-logo1.svg"
              alt="MezePay"
              className="h-12 w-auto opacity-40"
            />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex flex-col flex-1 gap-4 lg:gap-6 p-5 lg:p-6">
        <div className="flex flex-col flex-1 gap-3 lg:gap-4">
          {/* Tag & Read Time */}
          <div className="flex items-center gap-3 lg:gap-4">
            <span className="rounded-full border border-black/15 px-2.5 py-1 text-xs lg:text-sm font-semibold text-black">
              {category}
            </span>
            <span className="text-xs lg:text-sm font-semibold text-black">
              {readTime}
            </span>
          </div>

          {/* Title & Description */}
          <div className="flex flex-col gap-1.5 lg:gap-2">
            <h3 className="text-lg md:text-[28px] font-medium leading-[1.4] tracking-[-0.28px] text-black line-clamp-2">
              {title}
            </h3>
            <p className="text-sm lg:text-base font-normal leading-relaxed text-black line-clamp-2">
              {description}
            </p>
          </div>
        </div>

        {/* Read More */}
        <div className="flex items-center gap-2 mt-auto">
          <span className="text-base font-medium text-black group-hover:underline">
            {t("blog.readMore")}
          </span>
          <ChevronRight className="h-5 w-5 text-black" />
        </div>
      </div>
    </Link>
  );
}

/* -------------------------------------------------------------------------- */
/*                            Filter Tabs                                     */
/* -------------------------------------------------------------------------- */

interface FilterTabsProps {
  activeFilter: BlogCategory | "all";
  onFilterChange: (filter: BlogCategory | "all") => void;
}

function FilterTabs({ activeFilter, onFilterChange }: FilterTabsProps) {
  const { t } = useClientTranslation();
  return (
    <div className="w-full overflow-x-auto scrollbar-hide lg:w-auto lg:overflow-visible">
      <div className="flex items-center justify-start lg:justify-center whitespace-nowrap">
        <button
          onClick={() => onFilterChange("all")}
          className={`shrink-0 rounded-full px-4 py-2.5 text-sm lg:text-base transition-colors ${
            activeFilter === "all"
              ? "border border-black/15 bg-white font-medium text-black"
              : "font-normal text-black hover:bg-black/5"
          }`}
        >
          {t("blog.viewAll")}
        </button>
        {blogCategories.map((category) => (
          <button
            key={category}
            onClick={() => onFilterChange(category)}
            className={`shrink-0 rounded-full px-4 py-2.5 text-sm lg:text-base transition-colors ${
              activeFilter === category
                ? "border border-black/15 bg-white font-medium text-black"
                : "font-normal text-black hover:bg-black/5"
            }`}
          >
            {t(`blog.categories.${category}`)}
          </button>
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                              Blog Page                                     */
/* -------------------------------------------------------------------------- */

export default function BlogPage() {
  const { t, i18n } = useClientTranslation();
  const [activeFilter, setActiveFilter] = useState<BlogCategory | "all">(
    "all"
  );

  // Only show posts that match the current site language
  const langPosts = blogPosts.filter(
    (post) => post.language === i18n.language
  );

  const filteredPosts =
    activeFilter === "all"
      ? langPosts
      : langPosts.filter((post) => post.category === activeFilter);

  return (
    <PageLayout activePage="blog">
      <main className="flex-1">
        {/* Blog Section */}
        <section className="w-full bg-white px-5 md:px-px-tablet lg:px-px-desktop py-12 lg:py-section-y">
          <div className="flex flex-col items-center gap-10 lg:gap-gap-section max-w-container mx-auto">
            {/* Section Header */}
            <div className="flex flex-col items-center gap-3 lg:gap-4 max-w-content text-center">
              <span className="text-base font-semibold leading-relaxed text-black">
                {t("blog.featured")}
              </span>
              <div className="flex flex-col items-center gap-4 lg:gap-6 w-full">
                <h1 className="text-3xl md:text-5xl lg:text-[72px] font-medium leading-[1.2] tracking-tighter text-black">
                  {t("blog.title")}
                </h1>
                <p className="text-sm lg:text-lg font-normal leading-relaxed text-black">
                  {t("blog.subtitle")}
                </p>
              </div>
            </div>

            {/* Filters & Cards */}
            <div className="flex flex-col items-center gap-8 lg:gap-16 w-full">
              {/* Filter Tabs */}
              <FilterTabs
                activeFilter={activeFilter}
                onFilterChange={setActiveFilter}
              />

              {/* Blog Cards Grid */}
              <div className="flex flex-col gap-8 lg:gap-16 w-full">
                {/* Chunk posts into rows of 2 */}
                {Array.from(
                  { length: Math.ceil(filteredPosts.length / 2) },
                  (_, rowIndex) => (
                    <div
                      key={rowIndex}
                      className="flex flex-col lg:flex-row gap-8 lg:gap-12 w-full"
                    >
                      {filteredPosts
                        .slice(rowIndex * 2, rowIndex * 2 + 2)
                        .map((post) => (
                          <div key={post.slug} className="flex-1 min-w-0">
                            <BlogCard
                              slug={post.slug}
                              title={post.title}
                              description={post.description}
                              category={post.category}
                              readTime={post.readTime}
                              image={post.image}
                            />
                          </div>
                        ))}
                      {/* Add empty div if odd number of posts in last row */}
                      {filteredPosts.slice(rowIndex * 2, rowIndex * 2 + 2)
                        .length === 1 && (
                        <div className="flex-1 min-w-0" />
                      )}
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
    </PageLayout>
  );
}
